import { useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'

import { useCancelShipmentActiveOffer } from '#/shared/api/generated/shipment/theCargoShipment'
import {
  getApiErrorDetail,
  getHttpStatus,
} from '#/shared/lib/api/error-handler'
import { toast } from '#/shared/ui/sonner'

import { invalidateShipmentLists } from '#/entities/shipment'

type Translate = ReturnType<typeof useTranslation>['t']

// Map the cancel-active-offer contract's error codes to a message: 400 = SD not
// connected for the org, 409 = no active SD offer to cancel, 502 = SD rejected /
// unreachable (local row untouched, retry-safe). 404 carries a useful backend
// `detail`.
function cancelOfferErrorMessage(error: unknown, t: Translate): string {
  const status = getHttpStatus(error)
  if (status === 400) return t('detailSidebar.loadboard.sdNotConnected')
  if (status === 409) return t('detailSidebar.loadboard.cancelOfferNoRequest')
  if (status === 502) return t('detailSidebar.loadboard.sdUnreachable')
  return (
    getApiErrorDetail(error) ?? t('detailSidebar.loadboard.cancelOfferError')
  )
}

/**
 * Withdraws the active Super Dispatch offer on a dispatched-not-signed order
 * via POST /shipments/{id}/dispatch/cancel-active-offer — a dedicated action
 * that backs out only the active offer (shipment → `posted`, `carrier_id` null,
 * the accepted carrier request → canceled) while the SD order stays posted on
 * the board, so the shipment can immediately take a new offer. Distinct from the
 * dispatch cancel, which tears the whole order down.
 */
export function useCancelOffer(shipmentId: string) {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const mutation = useCancelShipmentActiveOffer()

  const cancelOffer = () => {
    mutation.mutate(
      { shipmentId },
      {
        onSuccess: () => {
          void queryClient.invalidateQueries({
            queryKey: [`/api/v1/shipments/${shipmentId}`],
          })
          void queryClient.invalidateQueries({
            queryKey: ['/api/v1/carrier-requests'],
          })
          invalidateShipmentLists(queryClient)
          toast.success(t('detailSidebar.loadboard.cancelOfferSuccess'))
        },
        onError: (error) => toast.error(cancelOfferErrorMessage(error, t)),
      },
    )
  }

  return { cancelOffer, isPending: mutation.isPending }
}
